import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  FormControl,
  Collapse,
  Typography,
  Alert,
  Box,
  TextField,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Select,
  InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import { CUSTOMERS_CREATE_ENDPOINT, CUSTOMERS_EDIT_ENDPOINT, SUBZONES_ENDPOINT } from '../../config/urls';
import dayjs from 'dayjs';
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import { useAuth } from '../../hooks/AuthProvider';
import typography from '../../cssUtils/typography';
import ButtonTwo from '../../common/dashboard-button';
import ButtonOne from '../../common/normal-button';
import { CallingCodeSelect } from '../../common/country-code-select';
import { getCountryForCallingCode } from '../../common/country-codes';
import { getPhoneValidationError } from '../../common/phone-validation';

const ZONES_API = `${SUBZONES_ENDPOINT}?limit=1000`;
const ADDRESS_NAME_OPTIONS = ["Home", "Office", "Family Home"];

// helper function to parse coordinates from a Google Maps URL or raw "lat,long" input
const parseCoordinates = (mapUrl) => {
  // Check if input is empty or just whitespace
  if (!mapUrl || mapUrl.trim() === '') {
    return { lat: '', lng: '', isValid: true }; // Empty input is valid (optional field)
  }

  const trimmed = mapUrl.trim();
  const isInRange = (lat, lng) =>
    !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;

  // Format 1: Google Maps URL — ?q=lat,long
  const urlMatch = trimmed.match(/[?&]q=(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)/);
  if (urlMatch) {
    const lat = parseFloat(urlMatch[1]);
    const lng = parseFloat(urlMatch[2]);
    if (isInRange(lat, lng)) {
      return { lat, lng, isValid: true };
    }
  }

  // Format 2: raw "lat, long"
  const rawMatch = trimmed.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
  if (rawMatch) {
    const lat = parseFloat(rawMatch[1]);
    const lng = parseFloat(rawMatch[2]);
    if (isInRange(lat, lng)) {
      return { lat, lng, isValid: true };
    }
  }

  // Format 3: Google Maps place/coordinate URL — .../@lat,lng,zoom
  // Prefer the precise pin location embedded as !3d<lat>!4d<lng> when present,
  // since the @lat,lng is just the viewport center and can differ slightly.
  const pinMatch = trimmed.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);
  if (pinMatch) {
    const lat = parseFloat(pinMatch[1]);
    const lng = parseFloat(pinMatch[2]);
    if (isInRange(lat, lng)) {
      return { lat, lng, isValid: true };
    }
  }

  const atMatch = trimmed.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*),/);
  if (atMatch) {
    const lat = parseFloat(atMatch[1]);
    const lng = parseFloat(atMatch[2]);
    if (isInRange(lat, lng)) {
      return { lat, lng, isValid: true };
    }
  }

  return {
    lat: '',
    lng: '',
    isValid: false,
    error: 'Invalid format. Use a Google Maps URL or raw "latitude,longitude"',
  };
};

// Helper function to format error messages descriptively
const formatErrorMessage = (errorResponse, operation) => {
  const errors = [];
  
  // Check for field-specific errors
  Object.entries(errorResponse).forEach(([field, messages]) => {
    if (messages) {
      const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      // Handle different message types
      if (typeof messages === 'string') {
        // Simple string
        errors.push(`${fieldName}: ${messages}`);
      } else if (Array.isArray(messages)) {
        // Check if array contains strings or objects
        messages.forEach((item, index) => {
          if (typeof item === 'string') {
            errors.push(`${fieldName}: ${item}`);
          } else if (typeof item === 'object' && item !== null) {
            // Handle address objects - group errors by address
            const addressErrors = [];
            Object.entries(item).forEach(([subField, subMessages]) => {
              if (subMessages) {
                const subFieldName = subField.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                const message = Array.isArray(subMessages) ? subMessages[0] : subMessages.toString();
                addressErrors.push(`${subFieldName}: ${message}`);
              }
            });
            if (addressErrors.length > 0) {
              errors.push(`Address ${index + 1} (${addressErrors.join(', ')})`);
            }
          }
        });
      } else if (typeof messages === 'object' && messages !== null) {
        // Handle nested objects - convert to string as fallback
        try {
          const objectErrors = JSON.stringify(messages, null, 0);
          errors.push(`${fieldName}: ${objectErrors}`);
        } catch (e) {
          errors.push(`${fieldName}: Validation error occurred`);
        }
      }
    }
  });
  
  if (errors.length > 0) {
    // Format as bullet points for better readability
    const bulletPoints = errors.map(error => `• ${error}`).join('\n');
    return `Failed to ${operation} customer:\n${bulletPoints}`;
  }
  
  return `Failed to ${operation} customer`;
};


const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const languageOptions = [
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'Arabic' },
];

const CustomerFormDialog = ({ modalState, handleModalState, selectedCustomer, refreshTable }) => {
  const { getValidToken } = useAuth();

  const [zones, setZones] = useState([]);
  const [addresses, setAddresses] = useState([]);

  const [alertObj, setAlertObj] = useState({
    active: false,
    msg: "",
    type: "",
  });

  const [formData, setFormData] = useState({
    name: '',
    country_code: '',
    dial_code: '',
    phone_no: '',
    email: '',
    date_of_birth: null,
    language_code: 'en',
  });

  const [formErrors, setFormErrors] = useState({
    name: '',
    country_code: '',
    dial_code: '',
    phone_no: '',
    email: '',
    date_of_birth: '',
    language_code: '',
    latitude: '',
    longitude: '',
    name: '',
    zone: '',
  });

  // Fetch zones/subzones on mount
  useEffect(() => {
    const tenant = localStorage.getItem("tenant");
    axios.get(ZONES_API, {
      headers: {
        "X-tenant-ID": tenant
      }
    }).then(res => {
      setZones(res.data.results);
    });
  }, []);

  // Populate addresses on edit
  useEffect(() => {
    if (modalState.mode === 'edit' && selectedCustomer?.addresses) {
      setAddresses(selectedCustomer.addresses.map(addr => ({
        ...addr,
        mapUrl: addr.location_url || '',
        mapUrlError: '' // Initialize error field
      })));
    }
  }, [modalState.mode, selectedCustomer]);

  // Handle address field changes
  const handleAddressChange = (idx, field, value) => {
    setAddresses(prev =>
      prev.map((addr, i) =>
        i === idx
          ? {
              ...addr,
              [field]: value,
              ...(field === 'mapUrl'
                ? (() => {
                    const result = parseCoordinates(value);
                    return {
                      latitude: result.lat,
                      longitude: result.lng,
                      location_url: value,
                      mapUrlError: result.isValid ? '' : (result.error || 'Invalid URL format')
                    };
                  })()
                : {})
            }
          : addr
      )
    );
  };
  
  useEffect(() => {
    if (selectedCustomer) {
      console.log("selectedCustomer: ", selectedCustomer);
      setFormData({
        name: selectedCustomer.name,
        country_code: selectedCustomer.country_code,
        dial_code: selectedCustomer.dial_code,
        phone_no: selectedCustomer.phone_no,
        email: selectedCustomer.email || '',
        date_of_birth: selectedCustomer.date_of_birth ? dayjs(selectedCustomer.date_of_birth) : null,
        language_code: selectedCustomer.language_code || '',
      });
    } else {
      setFormData({
        name: '',
        country_code: '971',
        dial_code: '',
        phone_no: '',
        email: '',
        date_of_birth: null,
        language_code: '',
      });
    }
  }, [selectedCustomer]);

  // Auto-close modal after 5 seconds for success messages
  useEffect(() => {
    if (alertObj.active && alertObj.type === 'success') {
      const timer = setTimeout(() => {
        handleClose();
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [alertObj]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

    const handleClose = () => {
    handleModalState({ isOpen: false });
    setAlertObj({ active: false, msg: '', type: '' });
    setFormErrors({
      name: '',
      country_code: '',
      dial_code: '',
      phone_no: '',
      email: '',
      date_of_birth: '',
      latitude: '',
      longitude: '',
      zone: '',
    });
    setAddresses([]);
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, date_of_birth: date });
  };

  const validateForm = async () => {
    const newFormErrors = {};
    let hasErrors = false;
    const emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

    if (formData.name.trim() === '' || formData.name.length > 250) {
      newFormErrors.name = 'Please enter a name (max 250 characters)';
      hasErrors = true;
    }

    if (formData.country_code.trim() === '') {
      newFormErrors.country_code = 'Please enter a country code';
      hasErrors = true;
    }

    if (formData.dial_code.trim() === '') {
      newFormErrors.dial_code = 'Please enter a dial code';
      hasErrors = true;
    }

    if (formData.email && !emailPattern.test(formData.email.trim())) {
      newFormErrors.email = 'Please enter a valid email';
      hasErrors = true;
    }

    if (!formData.language_code) {
      newFormErrors.language_code = 'Please select a language';
      hasErrors = true;
    }

    const dialCodeError = formData.country_code && formData.dial_code.trim()
      ? getPhoneValidationError(formData.dial_code, getCountryForCallingCode(formData.country_code), formData.country_code)
      : null;
    if (dialCodeError) {
      newFormErrors.dial_code = dialCodeError;
      hasErrors = true;
    }

    // NEW: Address validation for Google Maps URLs
    const addressErrors = {};
    addresses.forEach((addr, idx) => {
      if (addr.mapUrl && addr.mapUrl.trim() !== '') {
        const validation = parseCoordinates(addr.mapUrl);
        if (!validation.isValid) {
          addressErrors[`address_${idx}_mapUrl`] = validation.error || 'Invalid Google Maps URL format';
          hasErrors = true;
        }
      }
    });

    setFormErrors({ ...newFormErrors, ...addressErrors });
    return !hasErrors;
  };

  const handleAdd = async () => {
    if (!(await validateForm())) {
      return;
    }

    const formattedData = {
      ...formData,
      date_of_birth: formData.date_of_birth ? dayjs(formData.date_of_birth).format('YYYY-MM-DD') : null,
      addresses: addresses.map(addr => ({
        ...addr,
        // Remove mapUrl before sending to backend
        // mapUrl: undefined,
      })),
    };

    const token = await getValidToken();
    const tenant = localStorage.getItem('tenant');

    axios.post(CUSTOMERS_CREATE_ENDPOINT, formattedData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant
      },
    })
      .then(response => {
        setAlertObj({
          active: true,
          type: "success",
          msg: "Customer created successfully",
        });
        refreshTable(key => key + 1);
        setTimeout(() => {
          handleClose();
          setAlertObj({ active: false, type: "success", msg: "" });
        }, 3000);
      })
      .catch(error => {
        const errorResponse = error.response?.data || {};
        const newFormErrors = {
          name: errorResponse.name || '',
          country_code: errorResponse.country_code || '',
          dial_code: errorResponse.dial_code || '',
          phone_no: errorResponse.phone_no || '',
          email: errorResponse.email || '',
          date_of_birth: errorResponse.date_of_birth || '',
          language_code: errorResponse.language_code || '',
          latitude: errorResponse.latitude || '',
          longitude: errorResponse.longitude || '',
          zone: errorResponse.zone || '',
        };
        setFormErrors(newFormErrors);
        
        // Use descriptive error message
        const descriptiveError = formatErrorMessage(errorResponse, 'create');
        setAlertObj({
          active: true,
          type: "error",
          msg: descriptiveError,
        });
      });
  };

  const handleEdit = async () => {
    if (!(await validateForm())) {
      return;
    }

    const token = await getValidToken();
    const tenant = localStorage.getItem('tenant');

    const formattedData = {
      ...formData,
      date_of_birth: formData.date_of_birth ? dayjs(formData.date_of_birth).format('YYYY-MM-DD') : null,
      addresses: addresses.map(addr => ({
        ...addr,
        // Remove mapUrl before sending to backend
        // mapUrl: undefined,
      })),
    };

    axios.patch(`${CUSTOMERS_EDIT_ENDPOINT}${selectedCustomer.id}/`, formattedData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant
      },
    })
      .then(response => {
        setAlertObj({
          active: true,
          type: "success",
          msg: "Customer updated successfully",
        });
        refreshTable(key => key + 1);
        setTimeout(() => {
          handleClose();
          setAlertObj({ active: false, type: "success", msg: "" });
        }, 3000);
      })
      .catch(error => {
        const errorResponse = error.response?.data || {};
        const newFormErrors = {
          name: errorResponse.name || '',
          country_code: errorResponse.country_code || '',
          dial_code: errorResponse.dial_code || '',
          phone_no: errorResponse.phone_no || '',
          email: errorResponse.email || '',
          date_of_birth: errorResponse.date_of_birth || '',
          language_code: errorResponse.language_code || '',
        };
        setFormErrors(newFormErrors);
        
        // Use descriptive error message
        const descriptiveError = formatErrorMessage(errorResponse, 'update');
        setAlertObj({
          active: true,
          type: "error",
          msg: descriptiveError,
        });
      });
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={modalState.isOpen}
      maxWidth="sm"
      fullWidth
    >
    <DialogTitle sx={{ ...typography.h1 ,m: 0, p: 2, position: 'relative' }} id="customized-dialog-title">
         {modalState.mode === 'edit' ? 'Edit Customer' : 'Create Customer'}
         <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 0,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <FormControl fullWidth margin="normal">
          <TextField
            label="Name *"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!formErrors.name}
            helperText={formErrors.name}
          />
        </FormControl>
        <FormControl fullWidth margin="normal">
          <CallingCodeSelect
            label="Country Code *"
            value={formData.country_code}
            onChange={(code) => handleChange({ target: { name: 'country_code', value: code } })}
            TextFieldProps={{ error: !!formErrors.country_code, helperText: formErrors.country_code }}
          />
        </FormControl>
        <FormControl fullWidth margin="normal">
          <TextField
            label="Dial Code *"
            name="dial_code"
            value={formData.dial_code}
            onChange={handleChange}
            error={!!formErrors.dial_code}
            helperText={formErrors.dial_code}
            onKeyDown={(e) => {
              if (!/^\d$/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete") {
                e.preventDefault();
              }
            }}
          />
        </FormControl>
        <FormControl fullWidth margin="normal">
          <TextField
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            error={!!formErrors.email}
            helperText={formErrors.email}
          />
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel id="language-code-label">Language *</InputLabel>
          <Select
            labelId="language-code-label"
            label="Language *"
            name="language_code"
            value={formData.language_code}
            onChange={handleChange}
            error={!!formErrors.language_code}
          >
            {languageOptions.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="error">
            {formErrors.language_code}
          </Typography>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date of Birth"
              value={formData.date_of_birth}
              onChange={handleDateChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!formErrors.date_of_birth}
                  helperText={formErrors.date_of_birth}
                />
              )}
            />
          </LocalizationProvider>

           {/* Display Addresses in Edit Mode as Cards */}
              {(modalState.mode === 'edit' || modalState.mode === 'add') && (

                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    Addresses
                  </Typography>
                  <Grid container spacing={2}>
                    {addresses.map((address, idx) => {
                      // Exclude already used address names for other addresses
                      const usedNames = addresses
                        .filter((a, i) => i !== idx)
                        .map(a => a.name);
                      // const availableNames = ADDRESS_NAME_OPTIONS.filter(opt => !usedNames.includes(opt));
                      // no filter
                      const availableNames = ADDRESS_NAME_OPTIONS;
                      return (
                      <Grid item xs={12} key={address.id || idx}>
                        <Card variant="outlined" sx={{ position: 'relative' }}>
                          {/* Cross button to remove address */}
                          {addresses.length > 1 && (
                            <IconButton
                              aria-label="remove address"
                              size="small"
                              onClick={() => {
                                setAddresses(addresses.filter((_, i) => i !== idx));
                              }}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 1,
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                          <CardContent>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={11}>
                                <FormControl fullWidth>
                                  <InputLabel id={`address-name-label-${idx}`}>Address Name</InputLabel>
                                  <Select
                                    labelId={`address-name-label-${idx}`}
                                    value={address.name || ''}
                                    label="Address Name"
                                    onChange={e => handleAddressChange(idx, 'name', e.target.value)}
                                    error={!!formErrors.name}
                                    helperText={formErrors.name}
                                  >
                                    {availableNames.map(opt => (
                                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              {/* line break */}
                              <Grid item xs={12}></Grid>
                              <Grid item xs={12} sm={12}>
                                <TextField
                                  label="Google Maps URL"
                                  value={address.mapUrl || ''}
                                  onChange={e => handleAddressChange(idx, 'mapUrl', e.target.value)}
                                  fullWidth
                                  error={!!formErrors[`address_${idx}_mapUrl`] || !!address.mapUrlError}
                                  helperText={
                                    formErrors[`address_${idx}_mapUrl`] || 
                                    address.mapUrlError || 
                                    'Format: https://maps.google.com/?q=latitude,longitude'
                                  }
                                  placeholder="https://maps.google.com/?q=18.985919,72.816198"
                                />
                              </Grid>
                              <Grid item xs={6} sm={6}>
                                <TextField
                                  label="Latitude"
                                  value={address.latitude || ''}
                                  InputProps={{ readOnly: true }}
                                  disabled
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={6} sm={6}>
                                <TextField
                                  label="Longitude"
                                  value={address.longitude || ''}
                                  InputProps={{ readOnly: true }}
                                  disabled
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                  <InputLabel id={`zone-${idx}`}>Zone</InputLabel>
                                  <Select
                                    label="Zone"
                                    value={address.zone || ''}
                                    onChange={e => handleAddressChange(idx, 'zone', e.target.value)}
                                    fullWidth
                                    displayEmpty
                                  >
                                    {/* <MenuItem value="">Select Zone</MenuItem> */}
                                    {[...new Set(zones.map(z => z.zone?.name).filter(Boolean))].map(zoneName => (
                                      <MenuItem key={zoneName} value={zoneName}>{zoneName}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                  <InputLabel id={`subzone-${idx}`}>Subzone</InputLabel>
                                  <Select
                                    label="Subzone"
                                    value={address.subzone || ''}
                                    onChange={e => handleAddressChange(idx, 'subzone', e.target.value)}
                                    error={!!formErrors.zone}
                                    helperText={formErrors.zone}
                                    fullWidth
                                    displayEmpty
                                  >
                                    {/* <MenuItem value="">Select Subzone</MenuItem> */}
                                    {zones
                                      .filter(z => z.zone?.name === address.zone)
                                      .map(sub => (
                                        <MenuItem key={sub.id} value={sub.name}>{sub.name}</MenuItem>
                                      ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="Appartment / Vila Details"
                                  value={address.appartment_details || ''}
                                  onChange={e => handleAddressChange(idx, 'appartment_details', e.target.value)}
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="Building Details"
                                  value={address.building_details || ''}
                                  onChange={e => handleAddressChange(idx, 'building_details', e.target.value)}
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="Street Details"
                                  value={address.street_details || ''}
                                  onChange={e => handleAddressChange(idx, 'street_details', e.target.value)}
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="Landmark"
                                  value={address.landmark_details || ''}
                                  onChange={e => handleAddressChange(idx, 'landmark_details', e.target.value)}
                                  fullWidth
                                />
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                      );
                    })}
                  </Grid>
                  {/* Add Address Button */}
                  {addresses.length === 0 ? (
                    <Box sx={{ mt: 2 }}>
                      <ButtonTwo
                        variant="outlined"
                        onClick={() => setAddresses([{
                          name: '',
                          latitude: '',
                          longitude: '',
                          zone: '',
                          subzone: '',
                          appartment_details: '',
                          building_details: '',
                          street_details: '',
                          landmark_details: '',
                          mapUrl: '',
                          mapUrlError: ''
                        }])}
                      >
                        Add Address
                      </ButtonTwo>
                    </Box>
                  ) : (
                    // Added address limit of 9 - because chatbot has limit of 9
                    addresses.length < 9 && (
                      <Box sx={{ mt: 2 }}>
                        <ButtonTwo
                          variant="outlined"
                          onClick={() => {
                            const usedNames = addresses.map(a => a.name);
                            const availableNames = ADDRESS_NAME_OPTIONS.filter(opt => !usedNames.includes(opt));
                            setAddresses([
                              ...addresses,
                              {
                                name: availableNames[0] || '',
                                latitude: '',
                                longitude: '',
                                zone: '',
                                subzone: '',
                                appartment_details: '',
                                building_details: '',
                                street_details: '',
                                landmark_details: '',
                                mapUrl: '',
                                mapUrlError: ''
                              }
                            ]);
                          }}
                        >
                          Add Address
                        </ButtonTwo>
                      </Box>
                    )
                  )}
                </Box>
              )}


        </FormControl>
        <Collapse in={alertObj.active}>
          <Alert severity={alertObj.type} onClose={() => setAlertObj({ active: false })}>
            {alertObj.msg}
          </Alert>
        </Collapse>

      </DialogContent>

      <DialogActions>
         <ButtonTwo onClick={handleClose}>
           Cancel
         </ButtonTwo>
         {modalState.mode === 'edit' ? (
           <ButtonOne onClick={handleEdit} color="primary" variant="contained">
             Update
           </ButtonOne>
         ) : (
           <ButtonOne onClick={handleAdd} color="primary" variant="contained">
             Add
           </ButtonOne>
         )}
       </DialogActions>
    </BootstrapDialog>
  );
};

export default CustomerFormDialog;
